import AppContext from 'App/AppContext';
import Task from 'models/tasks/task';
import { TaskDetailView, TaskDetailViewMode, toTaskDetailViewMode } from 'models/userSetting';
import { useContext, useRef, useState } from 'react';
import { getLocalStorageData, LocalStorageKeys, setLocalStorageData } from 'utils/localstorage';

import { getShownTaskPivotKeys, getTaskPivotTabs, isNewTaskDetailView } from './taskPivotTabs';

export interface ITaskDetailView {
  viewMode: TaskDetailViewMode;
  //the redesigned dialog is shown for this task
  isNewView: boolean;
  //the tab the new view opens (the one the user used last when this task shows it, Details
  //otherwise); undefined when the task is not shown in the new view
  getNewViewPivot: (task: Task) => string | undefined;
  //remembers the tab the user picked in the new view
  rememberPivot: (pivot: string) => void;
  //saves the user setting and lets the caller pick the tab that fits the new layout
  changeClassicView: (classic: boolean) => void;
}

//Owns the task detail view mode (user setting), the new-view tab (localStorage) and the switch
//between the classic and the new view. onViewChanged runs after a switch, with the new mode
//already in effect.
export const useTaskDetailView = (task: Task, onViewChanged: () => void): ITaskDetailView => {
  const appContext = useContext(AppContext);
  const readViewMode = () =>
    toTaskDetailViewMode(appContext.globalDataCache.userSettings.get(TaskDetailView) as string | undefined);
  const [viewMode, setViewMode] = useState<TaskDetailViewMode>(readViewMode);
  //the mode of the latest switch, readable in the same tick (state is not updated yet there)
  const viewModeRef = useRef<TaskDetailViewMode>(viewMode);
  viewModeRef.current = viewMode;

  const isNewView = isNewTaskDetailView(viewMode, appContext.isMobileView, task.taskId);

  const getNewViewPivot = (forTask: Task): string | undefined => {
    if (!isNewTaskDetailView(viewModeRef.current, appContext.isMobileView, forTask.taskId)) return;

    const storedPivot = getLocalStorageData(appContext, LocalStorageKeys.TaskDetailPivot);
    //only restore a tab this task actually shows
    const newViewPivots = getShownTaskPivotKeys(getTaskPivotTabs(forTask, { isNewView: true }));

    return storedPivot && newViewPivots.includes(storedPivot) ? storedPivot : 'TaskDetails';
  };

  const rememberPivot = (pivot: string) => {
    setLocalStorageData(appContext, LocalStorageKeys.TaskDetailPivot, pivot, false);
  };

  const changeClassicView = (classic: boolean) => {
    const newView: TaskDetailViewMode = classic ? 'classic' : 'new';
    viewModeRef.current = newView;
    setViewMode(newView);
    void appContext.globalDataCache.userSettings.save(TaskDetailView, newView);
    onViewChanged();
  };

  return { viewMode, isNewView, getNewViewPivot, rememberPivot, changeClassicView };
};
